import React, { useEffect, useRef, useState } from 'react';
import { Type as TypeIcon, X } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { loadPdfDocument, TextEdit } from '../utils/pdfProcessor';
import { makeTextEditId } from '../utils/textEdit';

/**
 * "Cover and replace" text editor: click any text run rendered from the
 * page's real text layer to turn it into a normal text input — click to
 * place the caret, drag/shift-click to select, type over the selection,
 * backspace — all native browser text-field behavior, not a custom editor.
 * Committing a change doesn't touch the PDF yet; it just updates this
 * modal's local list. Saving hands the accumulated edits back to the
 * caller, which is what actually bakes them into the page (see
 * utils/textEdit.ts) at export time.
 *
 * Only text that already exists as real PDF text objects is clickable —
 * scanned/image-only pages have nothing here, since there's no text layer
 * to edit (see utils/textEdit.ts's module doc for why this is cover-and-
 * replace rather than a true content edit).
 *
 * Hit-region geometry comes from pdf.js's own TextLayer (src/display/
 * text_layer.js — the same code that renders the selectable/copyable text
 * overlay in every pdf.js-based viewer), not hand-computed math from the
 * raw item transform: a from-scratch calculation kept getting ascent/
 * descent subtly wrong, which is what made an earlier version feel
 * "erratic". TextLayer.textDivs and TextLayer.textContentItemsStr are
 * guaranteed 1:1 with each other and with the *filtered* textContent.items
 * (pdf.js only ever skips an item when `item.str === undefined` — a
 * marked-content boundary marker, not real text; empty/whitespace-only
 * strings still get a real div). Matching that exact filter is essential —
 * filtering more aggressively (e.g. dropping whitespace-only items before
 * zipping the two arrays together) desyncs the indices from that point
 * onward, which is what silently broke every click past the first
 * whitespace run in an earlier version of this file.
 */

interface TextEditModalProps {
  sourceBuffer: ArrayBuffer;
  pageIndex: number;
  rotation: number;
  existingEdits: TextEdit[];
  onSave: (edits: TextEdit[]) => void;
  onClose: () => void;
}

interface HitRegion {
  key: string;
  text: string;
  originalText: string;
  x: number; y: number; width: number; height: number; // PDF user-space, for baking
  fontSize: number;
  fontFamilyHint: string;
  screenLeft: number; screenTop: number; screenWidth: number; screenHeight: number;
  editId?: string;
  backgroundColor: [number, number, number];
  textColor: [number, number, number];
}

/** Rounds a channel to the nearest bucket of 16 so near-identical
 * anti-aliased pixels count as the same color when voting for the mode. */
function quantizeChannel(v: number): number {
  return Math.round(v / 16) * 16;
}

/**
 * Guesses the page background color behind a text run by sampling thin
 * strips just outside its bounding box (above/below/left/right) and taking
 * the most common color among them — background pixels vastly outnumber
 * any stray glyph-edge pixels a strip might clip, so the mode is a robust
 * stand-in for "the page color here" without needing to know anything about
 * how the page was drawn (solid fill, image, gradient, whatever).
 *
 * Reads from a single whole-page pixel buffer fetched once up front (see
 * caller) rather than calling ctx.getImageData() per text run — with
 * hundreds of runs on a page, doing that per-run (x4 strips each) was slow
 * enough, and occasionally throw-y enough, to abort the whole hit-region
 * build partway through — the earlier "clicking does nothing" regression.
 */
function sampleBackgroundColor(
  pixels: Uint8ClampedArray, canvasW: number, canvasH: number,
  left: number, top: number, width: number, height: number,
): [number, number, number] {
  const margin = 3;
  const clamp = (v: number, max: number) => Math.max(0, Math.min(max, Math.round(v)));
  const strips: Array<[number, number, number, number]> = [
    [left, top - margin, width, margin],   // above
    [left, top + height, width, margin],   // below
    [left - margin, top, margin, height],  // left of
    [left + width, top, margin, height],   // right of
  ];

  const counts = new Map<string, { count: number; r: number; g: number; b: number }>();
  for (const [sx, sy, sw, sh] of strips) {
    const x0 = clamp(sx, canvasW - 1);
    const y0 = clamp(sy, canvasH - 1);
    const w = Math.max(0, Math.min(canvasW - x0, Math.round(sw)));
    const h = Math.max(0, Math.min(canvasH - y0, Math.round(sh)));
    if (w <= 0 || h <= 0) continue;
    for (let y = y0; y < y0 + h; y++) {
      const rowOffset = y * canvasW * 4;
      for (let x = x0; x < x0 + w; x++) {
        const i = rowOffset + x * 4;
        if (pixels[i + 3] < 200) continue; // skip transparent pixels
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
        const key = `${quantizeChannel(r)},${quantizeChannel(g)},${quantizeChannel(b)}`;
        const entry = counts.get(key);
        if (entry) { entry.count++; entry.r += r; entry.g += g; entry.b += b; }
        else counts.set(key, { count: 1, r, g, b });
      }
    }
  }

  let best: { count: number; r: number; g: number; b: number } | null = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  if (!best) return [1, 1, 1];
  return [best.r / best.count / 255, best.g / best.count / 255, best.b / best.count / 255];
}

/** Black or white, whichever contrasts more with the given background. */
function contrastingTextColor(bg: [number, number, number]): [number, number, number] {
  const luminance = 0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2];
  return luminance > 0.6 ? [0, 0, 0] : [1, 1, 1];
}

export const TextEditModal: React.FC<TextEditModalProps> = ({
  sourceBuffer, pageIndex, rotation, existingEdits, onSave, onClose,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [regions, setRegions] = useState<HitRegion[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let textLayer: any = null;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const pdfDoc = await loadPdfDocument(sourceBuffer);
        if (!active) return;
        const page = await pdfDoc.getPage(pageIndex + 1);
        if (!active) return;

        const rot = (page.rotate + rotation) % 360;
        const native = page.getViewport({ scale: 1, rotation: rot });
        const containerW = Math.min(containerRef.current?.clientWidth || 800, 900);
        const scale = Math.max(containerW / native.width, 0.1);
        const viewport = page.getViewport({ scale, rotation: rot });

        const canvas = canvasRef.current;
        const layerDiv = layerRef.current;
        if (!canvas || !layerDiv) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!active) return;

        // Fetched once for the whole page — see sampleBackgroundColor's doc
        // for why per-run getImageData() calls caused clicks to stop working.
        let pixels: Uint8ClampedArray | null = null;
        try {
          pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        } catch (pixelErr) {
          console.warn('TextEditModal: could not read page pixels, patches will be plain white', pixelErr);
          pixels = null; // e.g. a tainted canvas — falls back to white patches below
        }

        layerDiv.innerHTML = '';
        layerDiv.style.width = `${viewport.width}px`;
        layerDiv.style.height = `${viewport.height}px`;
        layerDiv.style.setProperty('--scale-factor', String(viewport.scale));

        const textContent = await page.getTextContent();
        textLayer = new (pdfjsLib as any).TextLayer({
          textContentSource: textContent,
          container: layerDiv,
          viewport,
        });
        await textLayer.render();
        if (!active) return;

        // Must match pdf.js's own filter exactly (only `str === undefined` is
        // skipped) so this array stays index-aligned with textDivs — see the
        // module doc above for why this matters.
        const items = (textContent.items as any[]).filter(it => it.str !== undefined);
        const divs: HTMLElement[] = textLayer.textDivs;
        const layerRect = layerDiv.getBoundingClientRect();

        const newRegions: HitRegion[] = [];
        divs.forEach((div, idx) => {
          try {
            const item = items[idx];
            if (!item || !item.str || !item.str.trim()) return; // nothing to click on whitespace-only runs
            const rect = div.getBoundingClientRect();
            const ux = item.transform[4];
            const uy = item.transform[5];
            const existing = existingEdits.find(
              e => e.originalText === item.str && Math.abs(e.x - ux) < 1 && Math.abs(e.y - uy) < 1,
            );
            const fontSize = Math.hypot(item.transform[2], item.transform[3]) || item.height || 10;
            const screenLeft = rect.left - layerRect.left;
            const screenTop = rect.top - layerRect.top;
            const screenWidth = Math.max(rect.width, 6);
            const screenHeight = Math.max(rect.height, 6);
            const backgroundColor: [number, number, number] = pixels
              ? sampleBackgroundColor(pixels, canvas.width, canvas.height, screenLeft, screenTop, screenWidth, screenHeight)
              : [1, 1, 1];
            newRegions.push({
              key: `t${idx}`,
              text: existing?.newText ?? item.str,
              originalText: item.str,
              x: ux, y: uy, width: item.width, height: item.height,
              fontSize,
              fontFamilyHint: textContent.styles?.[item.fontName]?.fontFamily || '',
              screenLeft, screenTop, screenWidth, screenHeight,
              editId: existing?.id,
              backgroundColor,
              textColor: contrastingTextColor(backgroundColor),
            });
          } catch (itemErr) {
            // One bad item (odd transform, detached div, etc.) must not take
            // down every other clickable region on the page.
            console.warn('TextEditModal: skipped one text item while building hit regions', itemErr);
          }
        });
        console.info(`TextEditModal: page ${pageIndex}: ${items.length} text items, ${divs.length} textDivs, ${newRegions.length} clickable regions built`);
        if (active) setRegions(newRegions);
      } catch (e: any) {
        console.error('TextEditModal: failed to build the editor for this page', e);
        if (active) setError(e?.message || 'Failed to load this page for editing.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
      try { textLayer?.cancel(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceBuffer, pageIndex, rotation]);

  const commitRegion = (key: string, value: string) => {
    setRegions(prev => prev.map(r => (r.key === key ? { ...r, text: value } : r)));
    setEditingKey(null);
  };

  const handleSave = () => {
    const edits: TextEdit[] = regions
      .filter(r => r.text !== r.originalText)
      .map(r => ({
        id: r.editId || makeTextEditId(),
        originalText: r.originalText,
        newText: r.text,
        x: r.x, y: r.y, width: r.width, height: r.height,
        fontSize: r.fontSize,
        fontFamilyHint: r.fontFamilyHint,
        backgroundColor: r.backgroundColor,
        textColor: r.textColor,
      }));
    onSave(edits);
  };

  const editedCount = regions.filter(r => r.text !== r.originalText).length;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg-card)', borderRadius: 14, width: 'min(960px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--separator)' }}>
          <TypeIcon size={16} style={{ color: 'var(--accent)' }} />
          <b style={{ fontSize: 14, flex: 1 }}>Edit text</b>
          <button className="btn-icon" onClick={onClose} title="Close"><X size={15} /></button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, overflowY: 'auto' }}>
          {error ? (
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</p>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', margin: 0 }}>
                Click any text to edit it — select and type, or backspace and retype, just like a text field.
                Scanned pages with no real text layer have nothing clickable here.
              </p>
              <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', minHeight: loading ? 300 : undefined }}>
                {loading && <div className="spinner" style={{ marginTop: 100 }} />}
                {/* visibility, not display: a display:none subtree collapses
                    layout entirely, so every getBoundingClientRect() measured
                    while building hit regions above would read back a zero
                    rect — that's what stacked every clickable region into a
                    tiny 6x6 box at the top-left corner instead of over the
                    real text. visibility:hidden keeps real layout geometry
                    while still hiding the canvas until it's ready to show. */}
                <div style={{ position: 'relative', visibility: loading ? 'hidden' : 'visible' }}>
                  <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }} />
                  {/* pdf.js's real text layer — invisible, exists only so we can
                      measure exact per-run rects off its rendered spans. */}
                  <div ref={layerRef} className="pdfpager-text-measure-layer" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', opacity: 0 }} />
                  {regions.map(r => (
                    editingKey === r.key ? (
                      <input
                        key={r.key}
                        autoFocus
                        defaultValue={r.text}
                        style={{
                          position: 'absolute',
                          left: r.screenLeft, top: r.screenTop,
                          width: r.screenWidth, height: r.screenHeight,
                          fontSize: Math.max(r.screenHeight * 0.82, 10),
                          lineHeight: `${r.screenHeight}px`,
                          fontFamily: r.fontFamilyHint || 'sans-serif',
                          border: '1px solid var(--accent)', outline: 'none',
                          background: '#fff', color: '#000', padding: 0, margin: 0,
                          boxSizing: 'border-box', zIndex: 2,
                        }}
                        onFocus={e => e.target.select()}
                        onBlur={e => commitRegion(r.key, e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                          if (e.key === 'Escape') { e.preventDefault(); setEditingKey(null); }
                        }}
                      />
                    ) : (
                      <div
                        key={r.key}
                        title={r.text !== r.originalText ? `"${r.originalText}" → "${r.text}"` : 'Click to edit'}
                        onClick={() => setEditingKey(r.key)}
                        style={{
                          position: 'absolute',
                          left: r.screenLeft, top: r.screenTop,
                          width: r.screenWidth, height: r.screenHeight,
                          cursor: 'text',
                          background: r.text !== r.originalText ? 'rgba(52,199,89,0.22)' : 'transparent',
                          border: r.text !== r.originalText ? '1px solid rgba(52,199,89,0.7)' : '1px solid transparent',
                        }}
                        onMouseEnter={e => {
                          if (r.text === r.originalText) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,122,255,0.15)';
                        }}
                        onMouseLeave={e => {
                          if (r.text === r.originalText) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                        }}
                      />
                    )
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderTop: '1px solid var(--separator)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}>
            {editedCount > 0 ? `${editedCount} change${editedCount === 1 ? '' : 's'}` : 'No changes yet'}
          </span>
          <button className="btn-sm btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-sm btn-primary" onClick={handleSave} disabled={editedCount === 0 && existingEdits.length === 0}>
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};
